import { motion } from 'framer-motion';
import { CheckIcon } from '@heroicons/react/24/outline';

interface Column {
  badge: string;
  heading: string;
  description: string;
  features: string[];
}

const COLUMNS: Column[] = [
  {
    badge: 'For Sponsees',
    heading: 'Share your journey',
    description:
      'Share check-ins, step work, and homework with your sponsor. They see what you choose to share \u2014 nothing more.',
    features: [
      'Share daily check-ins',
      'Submit step work for review',
      'Receive and complete homework',
      'Send SOS alerts',
    ],
  },
  {
    badge: 'For Sponsors',
    heading: 'See your people\u2019s progress',
    description:
      'Dashboard with prot\u00e9g\u00e9 activity, homework review, step progress tracking. Send homework, review step work, stay connected between meetings.',
    features: [
      'Sponsor dashboard with all prot\u00e9g\u00e9s',
      'Assign and review homework',
      'Review step work and give feedback',
      'Receive SOS alerts immediately',
    ],
  },
];

const FEATURE_PILLS = [
  'Shared check-ins',
  'Homework system',
  'Step review',
  'SOS alerts',
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

export function SponsorSection() {
  return (
    <section
      id="sponsor"
      className="bg-[#0D1321] py-20 px-4"
    >
      <div className="max-w-6xl mx-auto">
        {/* Heading */}
        <motion.div
          className="text-center mb-14"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Stay connected with your sponsor.
          </h2>
        </motion.div>

        {/* Two-column layout */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {COLUMNS.map((col) => (
            <motion.div
              key={col.heading}
              variants={fadeUp}
              className="rounded-2xl bg-white/5 border border-white/10 p-8 flex flex-col gap-5"
            >
              {/* Badge */}
              <span className="inline-block self-start bg-teal-500/15 text-teal-300 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                {col.badge}
              </span>

              {/* Heading */}
              <h3 className="text-xl font-bold text-white">
                {col.heading}
              </h3>

              {/* Description */}
              <p className="text-slate-400 text-sm leading-relaxed">
                {col.description}
              </p>

              {/* Feature list */}
              <ul role="list" className="flex flex-col gap-3">
                {col.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-3">
                    <div className="p-1 rounded-full bg-teal-500/15 shrink-0">
                      <CheckIcon className="w-3.5 h-3.5 text-teal-400" />
                    </div>
                    <span className="text-sm text-slate-300">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        {/* Feature pills */}
        <motion.div
          className="flex flex-wrap items-center justify-center gap-3"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-40px' }}
        >
          {FEATURE_PILLS.map((pill) => (
            <motion.span
              key={pill}
              variants={fadeUp}
              className="bg-teal-500/15 text-teal-300 rounded-full px-4 py-1.5 text-sm font-medium"
            >
              {pill}
            </motion.span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
